const HIGHLIGHTS = [
  { icon: 'ri-fire-line', label: 'Indoor Fireplace', desc: 'Stone fireplace in the main living area' },
  { icon: 'ri-fire-fill', label: 'Outdoor Fireplace', desc: 'Wood-burning fireplace on the covered deck' },
  { icon: 'ri-home-4-line', label: 'Covered Deck', desc: 'Spacious deck with sweeping forest views' },
  { icon: 'ri-water-flash-line', label: 'Near Glen Falls', desc: 'Minutes from the Glen Falls trailhead' },
  { icon: 'ri-map-pin-line', label: 'Close to Downtown', desc: 'Short drive to Highlands restaurants & shops' },
  { icon: 'ri-heart-line', label: 'Pet Friendly', desc: 'Welcomes dogs — wooded setting ideal for pets' },
];

export default function FeaturedCabinSection() {
  return (
    <section className="bg-[#EDE8E0] py-24 px-6 lg:px-14">
      <div className="max-w-7xl mx-auto">

        {/* Section header */}
        <div className="flex items-center gap-4 mb-12">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#2D4A3E]">
              <i className="ri-award-line text-[#C9A96E] text-sm" />
            </div>
            <span className="text-[#2D4A3E] text-xs tracking-[0.35em] uppercase font-jost font-medium">Editor&apos;s Top Pick — #1 Rated Cabin in Highlands, NC</span>
          </div>
          <div className="flex-1 h-px bg-[#2D4A3E]/20" />
        </div>

        {/* Main card */}
        <article className="bg-white rounded-3xl overflow-hidden flex flex-col lg:flex-row">

          {/* Image */}
          <div className="lg:w-[50%] h-80 lg:h-auto min-h-[520px] overflow-hidden shrink-0 relative">
            <div className="absolute top-6 left-6 z-20 flex items-center gap-2 bg-[#2D4A3E] text-white text-xs tracking-[0.2em] uppercase font-jost px-4 py-2 rounded-full">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-award-line text-[#C9A96E] text-xs" />
              </div>
              Top Rated in Highlands
            </div>
            <div className="absolute bottom-6 left-6 z-20 bg-white/95 rounded-2xl px-5 py-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-0.5">
                  {[1,2,3,4,5].map((i) => (
                    <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-star-fill text-[#C9A96E] text-xs" />
                    </div>
                  ))}
                </div>
                <span className="font-cormorant text-lg text-[#2D4A3E] font-semibold">5.0</span>
                <span className="text-[#9C9890] text-xs font-jost">· Airbnb Guest Favorite</span>
              </div>
            </div>
            <img
              src="https://static.readdy.ai/image/ad5520ee7149a263b01f4e3410f2d685/ba0d51886e955561a1eff3aa82f68703.jpeg"
              alt="Glen Falls Luxury Cabin exterior in Highlands NC surrounded by forest"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Content */}
          <div className="flex-1 p-10 lg:p-14 flex flex-col">

            <span className="text-[#6B8C7D] text-xs tracking-widest uppercase font-jost block mb-3">
              Luxury Cabin &nbsp;&bull;&nbsp; Highlands, NC — Near Glen Falls Waterfall
            </span>
            <h2 className="font-cormorant text-5xl md:text-6xl text-[#1A1A18] font-medium leading-tight mb-6">
              Glen Falls Luxury Cabin<br />
              <span className="font-cormorant text-2xl text-[#6B8C7D] font-light not-italic">in Highlands, NC</span>
            </h2>

            <p className="text-[#5C5850] text-sm font-jost leading-relaxed mb-4">
              Glen Falls Luxury Cabin is one of the top-rated cabins in Highlands, NC — a private mountain retreat set in a peaceful wooded setting just minutes from Glen Falls waterfall and the heart of downtown Highlands. It offers something rare in the Highlands rental market: genuine seclusion paired with effortless access to everything the area has to offer.
            </p>
            <p className="text-[#5C5850] text-sm font-jost leading-relaxed mb-4">
              The cabin is finished to a high standard throughout — with luxury finishes, a stone indoor fireplace for cool mountain evenings, and an outdoor wood-burning fireplace on the covered deck for starlit nights in the forest. The covered deck itself is a standout feature: spacious, private, and surrounded by trees, it&apos;s the kind of space guests return to again and again throughout their stay.
            </p>
            <p className="text-[#5C5850] text-sm font-jost leading-relaxed mb-8">
              With a 5.0-star rating and Airbnb Guest Favorite recognition, it consistently ranks among the most sought-after cabin rentals in Highlands — and its combination of privacy, location, and quality makes it easy to understand why.
            </p>

            {/* Highlights grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
              {HIGHLIGHTS.map((h) => (
                <div key={h.label} className="flex items-start gap-3 bg-[#F5F1EB] rounded-xl p-3.5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white shrink-0 mt-0.5">
                    <i className={`${h.icon} text-[#2D4A3E] text-sm`} />
                  </div>
                  <div>
                    <p className="text-[#1A1A18] text-xs font-jost font-medium leading-snug">{h.label}</p>
                    <p className="text-[#9C9890] text-xs font-jost leading-snug mt-0.5">{h.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 mb-8">
              <a
                href="https://glenfallscabin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap inline-flex items-center gap-2.5 bg-[#2D4A3E] hover:bg-[#1e3329] text-white text-xs font-jost tracking-[0.18em] uppercase px-8 py-3.5 rounded-full cursor-pointer transition-colors duration-200"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-3-line text-sm" />
                </div>
                View Cabin Details
              </a>
              <a
                href="https://www.airbnb.com/rooms/900012473285450950"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap inline-flex items-center gap-2 border border-[#C9A96E] text-[#5C5850] text-xs font-jost tracking-[0.12em] uppercase px-6 py-3.5 rounded-full cursor-pointer hover:bg-[#C9A96E]/10 transition-colors duration-200"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-external-link-line text-xs" />
                </div>
                View on Airbnb
              </a>
              <a
                href="https://www.vrbo.com/3557955"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap inline-flex items-center gap-2 border border-[#C9A96E] text-[#5C5850] text-xs font-jost tracking-[0.12em] uppercase px-6 py-3.5 rounded-full cursor-pointer hover:bg-[#C9A96E]/10 transition-colors duration-200"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-external-link-line text-xs" />
                </div>
                Book on Vrbo
              </a>
            </div>

            {/* Footer row */}
            <div className="flex items-center justify-between pt-6 border-t border-[#F5F1EB] mt-auto">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5 text-[#6B8C7D] text-xs font-jost bg-[#EEF5EE] px-3 py-1.5 rounded-full">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-heart-line text-xs" />
                  </div>
                  Pet Friendly
                </span>
                <span className="flex items-center gap-1.5 text-[#3A6A8C] text-xs font-jost bg-[#EEF2F8] px-3 py-1.5 rounded-full">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-water-flash-line text-xs" />
                  </div>
                  Near Glen Falls
                </span>
              </div>
              <div className="text-right">
                <p className="text-[#9C9890] text-xs font-jost mb-0.5">Editor&apos;s top pick for</p>
                <p className="font-cormorant text-lg text-[#2D4A3E] font-medium italic">Cabins in Highlands NC</p>
              </div>
            </div>

          </div>
        </article>
      </div>
    </section>
  );
}
